import React from 'react';
import { Alert, Box, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { auth } from '../../services/firebase';
import { fetchMedicationOccurrence, recordMedicationAdministration } from '../../services/medicationReminderService';
import { useRole } from '../../contexts/RoleContext';
import { AUTH_STORAGE_KEYS } from '../../services/auth/constants';
import { isMedicationAdministrationResolved, MEDICATION_NOT_TAKEN_REASONS } from '../../features/medicationReminders/medicationAdministrationOptions';
import { buildMedicationTimelineNavigationState } from '../../features/medicationReminders/medicationTimelineNavigation';

const localDateTimeValue = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const formatTime = (value) => value?.toLocaleTimeString?.([], { hour: 'numeric', minute: '2-digit' }) || '';

const MedicationDosePage = () => {
  const { occurrenceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const { childrenWithAccess, loading: roleLoading, getUserRoleForChild } = useRole();
  const [occurrence, setOccurrence] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [status, setStatus] = React.useState('taken');
  const [reason, setReason] = React.useState('');
  const [note, setNote] = React.useState('');
  const [actualTakenAt, setActualTakenAt] = React.useState(localDateTimeValue());
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const childId = searchParams.get('childId') || occurrence?.childId || '';
  const requestedAction = searchParams.get('action');

  React.useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      localStorage.setItem(AUTH_STORAGE_KEYS.RETURN_URL, returnTo);
      sessionStorage.setItem(AUTH_STORAGE_KEYS.RETURN_URL, returnTo);
      navigate('/login', { replace: true, state: { returnTo } });
      return;
    }
    let active = true;
    fetchMedicationOccurrence(occurrenceId).then((item) => {
      if (!active) return;
      setOccurrence(item);
      if (item && isMedicationAdministrationResolved(item.status)) {
        setStatus(item.status); setReason(item.reason || ''); setNote(item.note || '');
        setActualTakenAt(localDateTimeValue(item.actualTakenAt || item.recordedAt || new Date()));
      }
      setLoading(false);
    }).catch(() => { if (active) { setError('This medication dose is not available.'); setLoading(false); } });
    return () => { active = false; };
  }, [authLoading, navigate, occurrenceId, user]);

  const child = childrenWithAccess.find((item) => item.id === childId);
  const role = childId ? getUserRoleForChild(childId) : null;
  const canEdit = ['care_owner', 'care_partner'].includes(role);
  const resolved = occurrence && isMedicationAdministrationResolved(occurrence.status);
  const medication = occurrence?.medicationSnapshot || {};
  const dose = [medication.dose, medication.unit].filter(Boolean).join(' ');

  const save = async () => {
    setSaving(true); setError('');
    try {
      const isCorrection = Boolean(resolved && editing);
      await recordMedicationAdministration(occurrenceId, {
        status, reason: status === 'not_taken' ? reason : '', note,
        allowCorrection: isCorrection,
        ...(status === 'taken' ? { actualTakenAt: new Date(actualTakenAt).toISOString() } : {}),
      });
      navigate('/dashboard', {
        replace: true,
        state: buildMedicationTimelineNavigationState({
          childId,
          occurrenceIds: [occurrenceId],
          scheduledDayKey: occurrence.scheduledDayKey,
          timelineEntryId: occurrence.medicationRoundId || '',
          updated: isCorrection,
        }),
      });
    } catch (nextError) { setError(nextError?.message || 'This medication record could not be saved.'); }
    finally { setSaving(false); }
  };

  if (authLoading || loading || roleLoading) return <Box sx={{ minHeight: '70dvh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (!occurrence || !child) return <Box sx={{ maxWidth: 620, mx: 'auto', p: 2 }}><Alert severity="info">This medication dose is no longer available.</Alert><Button onClick={() => navigate('/home')} sx={{ mt: 2 }}>Done</Button></Box>;

  return (
    <Box sx={{ maxWidth: 620, mx: 'auto', px: 2, py: 3 }}>
      <Button onClick={() => navigate('/home')} sx={{ mb: 2 }}>Done</Button>
      <Typography variant="overline" color="primary.main" sx={{ fontWeight: 900 }}>{child.name || 'Medication'} · medication check</Typography>
      <Typography variant="h4" sx={{ fontWeight: 900, mt: 0.5 }}>{medication.name || 'Medication'}</Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5 }}>{dose || 'Dose not listed'} · Scheduled {occurrence.scheduledTime || formatTime(occurrence.scheduledFor)}</Typography>
      {error ? <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert> : null}

      {resolved && !editing ? (
        <Stack spacing={2} sx={{ mt: 3, p: 2.5, bgcolor: '#F2F7F5', borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            {occurrence.status === 'taken' ? `Taken at ${formatTime(occurrence.actualTakenAt || occurrence.recordedAt)}` : occurrence.status === 'missed' ? 'Missed' : 'Not taken'}
          </Typography>
          <Typography color="text.secondary">Recorded by {occurrence.recordedBy === user.uid ? 'you' : 'another care partner'}.</Typography>
          {occurrence.reason ? <Typography>{MEDICATION_NOT_TAKEN_REASONS.find(([value]) => value === occurrence.reason)?.[1] || occurrence.reason}</Typography> : null}
          {canEdit ? <Button variant="outlined" onClick={() => setEditing(true)}>Edit record</Button> : null}
          <Button variant="text" onClick={() => navigate('/care-profile', { state: { childId, openChildMedicationManager: true } })}>Change reminder schedule</Button>
        </Stack>
      ) : canEdit ? (
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant={status === 'taken' ? 'contained' : 'outlined'} onClick={() => setStatus('taken')}>Yes, taken</Button>
            <Button variant={status === 'not_taken' ? 'contained' : 'outlined'} onClick={() => setStatus('not_taken')}>No, not taken</Button>
            <Button variant={status === 'missed' ? 'contained' : 'outlined'} onClick={() => setStatus('missed')}>Missed</Button>
          </Stack>
          {status === 'taken' ? <TextField label="Actual time taken" type="datetime-local" value={actualTakenAt} onChange={(event) => setActualTakenAt(event.target.value)} InputLabelProps={{ shrink: true }} /> : null}
          {status === 'not_taken' ? <TextField select label="Reason" value={reason} onChange={(event) => setReason(event.target.value)}>{MEDICATION_NOT_TAKEN_REASONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField> : null}
          {status !== 'taken' ? <TextField label="Short note (optional)" value={note} onChange={(event) => setNote(event.target.value)} inputProps={{ maxLength: 500 }} /> : null}
          {requestedAction === 'taken' && !resolved ? <Alert severity="info">Taken is selected. Confirm below to save it.</Alert> : null}
          <Button variant="contained" size="large" disabled={saving || (status === 'not_taken' && !reason)} onClick={save}>{resolved ? 'Save correction' : `Confirm ${status === 'taken' ? 'taken' : status === 'missed' ? 'missed' : 'not taken'}`}</Button>
          {editing ? <Button onClick={() => setEditing(false)}>Cancel</Button> : null}
          <Button variant="text" onClick={() => navigate('/care-profile', { state: { childId, openChildMedicationManager: true } })}>Change reminder schedule</Button>
        </Stack>
      ) : <Alert severity="info" sx={{ mt: 3 }}>A Care Owner or Care Partner can record this medication dose.</Alert>}
    </Box>
  );
};

export default MedicationDosePage;
